import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import { fetch_questions_in_project } from '@/services/question_service';
import {
  extract_question_choice_items,
  normalize_choice_key,
  normalize_question_type,
} from '@/services/risk_management_service';
import { extract_error_message } from '@/utils/api_response_helpers';
import {
  GET_CHECKLIST_WEIGHTS,
  GET_ASSIGNED_QUESTIONS_TO_ASSESSMENT,
  SAVE_CHECKLIST_WEIGHTS,
  get_request_config,
} from '@/utils/system_constants/constant.js';

const first_defined_value = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null) {
      return value;
    }
  }

  return undefined;
};

export default {
  name: 'ChecklistWeightings',
  mixins: [alert_mixin],
  props: {
    assessmentId: {
      type: [Number, String],
      default: null,
    },
  },
  data: () => ({
    checklist_questions: [],
    loading_questions: false,
    saving: false,
  }),
  computed: {
    total_questions() {
      return this.checklist_questions.length;
    },
    total_weight_inputs() {
      return this.checklist_questions.reduce((count, question) => count + question.options.length, 0);
    },
    has_questions() {
      return this.checklist_questions.length > 0;
    },
  },
  watch: {
    assessmentId: {
      immediate: true,
      handler() {
        this.load_questions();
      },
    },
  },
  methods: {
    get_question_record(question) {
      return question?.question && typeof question.question === 'object'
        ? question.question
        : question;
    },

    get_route_assessment_id() {
      const route_query = this.$route?.query || {};
      const query_keys = ['risk_framework_assessment_id'];
      const key = query_keys.find((item) =>
        Reflect.has(route_query, item)
      );
      return Number(key ? Reflect.get(route_query, key) : null);
    },

    current_assessment_id() {
      const prop_value = Number(this.assessmentId);
      if (Number.isFinite(prop_value) && prop_value > 0) {
        return prop_value;
      }

      const route_value = this.get_route_assessment_id();
      return Number.isFinite(route_value) && route_value > 0
        ? route_value
        : null;
    },

    build_question_request_payload() {
      const route_query = this.$route?.query || {};
      const payload = {};

      if (route_query.project_id) {
        payload.project_id = route_query.project_id;
      }

      if (route_query.form_id) {
        payload.form_id = route_query.form_id;
      }

      return payload;
    },

    normalize_question_id(value) {
      const question_id = Number(value);
      return Number.isFinite(question_id) && question_id > 0 ? question_id : null;
    },

    format_question_type_label(value) {
      return String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
        .trim();
    },

    get_question_type(question) {
      const source_question = this.get_question_record(question);
      const raw_type =
        source_question?.question_type?.type ||
        source_question?.question_type?.question_type ||
        source_question?.question_type ||
        source_question?.type ||
        '';

      return raw_type;
    },

    get_question_options(question) {
      const source_question = this.get_question_record(question);
      const option_items = extract_question_choice_items(source_question).map((option) => {
        return {
          ...option,
          weight: 0,
        };
      });

      const options = option_items.length
        ? option_items
        : [
            { id: 1, label: 'Option 1', weight: 0 },
            { id: 2, label: 'Option 2', weight: 0 },
          ];

      return options;
    },

    get_question_id(question, index) {
      const question_id = Number(
        first_defined_value(
          question?.question_id,
          typeof question?.question === 'number' ? question.question : null,
          typeof question?.question === 'string' && Number.isFinite(Number(question.question))
            ? Number(question.question)
            : null,
          question?.question?.id,
          question?.section_question_id,
          question?.form_section_question_id,
          question?.id,
          index + 1,
        ),
      );

      const final_id =
        Number.isFinite(question_id) && question_id > 0
          ? question_id
          : index + 1;

      return final_id;
    },

    is_supported_question_type(normalized_type) {
      const is_supported =
        normalized_type.includes('single_select') ||
        normalized_type.includes('multi_select');

      return is_supported;
    },

    get_question_label(question) {
      const source_question = this.get_question_record(question);
      return String(
        first_defined_value(
          source_question?.text,
          source_question?.question_text,
          source_question?.label,
          typeof question?.question === 'string' ? question.question : null,
          'Untitled Question',
        ),
      );
    },

    get_saved_choice_label(choice) {
      const label = first_defined_value(choice?.option, choice?.label, choice?.name, '');

      return normalize_choice_key(label);
    },

    get_saved_choice_id(choice) {
      const option_id = Number(
        first_defined_value(
          choice?.option_id,
          choice?.id,
          choice?.question_option_id,
          choice?.choice_id,
          null,
        ),
      );

      return Number.isFinite(option_id) && option_id > 0 ? option_id : null;
    },

    get_saved_choice_weight(choice) {
      return Number(choice?.weight) || 0;
    },

    add_saved_choice_to_map(accumulator, choice) {
      const option_id = this.get_saved_choice_id(choice);
      if (option_id) {
        accumulator.set(`id:${option_id}`, this.get_saved_choice_weight(choice));
      }

      const option_label = this.get_saved_choice_label(choice);
      if (!option_label) {
        return accumulator;
      }

      accumulator.set(`label:${option_label}`, this.get_saved_choice_weight(choice));

      return accumulator;
    },

    build_question_row(question, index = 0) {
      const raw_type = this.get_question_type(question);
      const normalized_type = normalize_question_type(raw_type);
      const is_supported = this.is_supported_question_type(normalized_type);

      let formatted_question = null;

      if (is_supported) {
        formatted_question = {
          id: this.get_question_id(question, index),
          question: this.get_question_label(question),
          type: this.format_question_type_label(raw_type),
          options: this.get_question_options(question),
        };
      }

      return formatted_question;
    },
    apply_saved_weights(saved_rows = []) {
      const saved_map = new Map(
        (Array.isArray(saved_rows) ? saved_rows : [])
          .map((row) => {
            const question_id = Number(
              first_defined_value(
                row?.question_id,
                row?.question?.id,
                row?.id,
                null,
              ),
            );
            if (!Number.isFinite(question_id) || question_id <= 0) return null;

            const choices = Array.isArray(row?.choices)
              ? row.choices
              : [row].filter((choice) => this.get_saved_choice_id(choice) || this.get_saved_choice_label(choice));
            const normalized_choices = choices.reduce(
              (accumulator, choice) => this.add_saved_choice_to_map(accumulator, choice),
              new Map(),
            );

            return [question_id, normalized_choices];
          })
          .filter(Boolean)
      );

      this.checklist_questions = this.checklist_questions.map((question) => {
        const question_weights = saved_map.get(Number(question.id));
        if (!question_weights) {
          return question;
        }

        return {
          ...question,
          options: question.options.map((option) => ({
            ...option,
            weight: question_weights.get(`id:${Number(option.id)}`)
              ?? question_weights.get(`label:${normalize_choice_key(option.label)}`)
              ?? option.weight,
          })),
        };
      });
    },
    async load_saved_weights() {
      const assessment_id = this.current_assessment_id();
      if (!assessment_id || !this.checklist_questions.length) {
        return;
      }

      try {
        const { data } = await axios.get(
          GET_CHECKLIST_WEIGHTS,
          get_request_config({ params: { assessment_id } }),
        );

        if (data?.status === 'success') {
          this.apply_saved_weights(Array.isArray(data.data) ? data.data : []);
        }
      } catch (error) {
        this.show_error(extract_error_message(error, 'Failed to load saved checklist weights.'));
      }
    },
    async load_questions() {
      this.loading_questions = true;

      try {
        const assessment_id = this.current_assessment_id();
        if (!assessment_id) {
          this.checklist_questions = [];
          return;
        }

        const { data } = await axios.get(
          GET_ASSIGNED_QUESTIONS_TO_ASSESSMENT,
          get_request_config({ params: { assessment_id } }),
        );

        const assigned_rows = data?.status === 'success' && Array.isArray(data.data)
          ? data.data
          : [];
        const assigned_question_ids = new Set(
          assigned_rows
            .map((row) => this.normalize_question_id(
              row?.question_id
              ?? (typeof row?.question === 'number' ? row.question : null)
              ?? row?.question?.id
              ?? row?.id,
            ))
            .filter((question_id) => question_id),
        );

        const project_questions = await fetch_questions_in_project(this.build_question_request_payload());

        this.checklist_questions = project_questions
          .filter((question) => assigned_question_ids.has(this.normalize_question_id(question?.id)))
          .map((question, index) => this.build_question_row(question, index))
          .filter(Boolean);
        await this.load_saved_weights();
      } catch (error) {
        this.checklist_questions = [];
        this.show_error(extract_error_message(error, 'Failed to load assigned checklist questions.'));
      } finally {
        this.loading_questions = false;
      }
    },
    save_checklist_weightings() {
      const assessment_id = this.current_assessment_id();
      if (!assessment_id) {
        this.show_error('Missing risk framework assessment id.');
        return;
      }

      const items = this.checklist_questions.flatMap((question) => (
        question.options.map((option) => ({
          question_id: question.id,
          option_id: option.id,
          weight: Number(option.weight) || 0,
        }))
      ));

      this.saving = true;

      axios.post(
          SAVE_CHECKLIST_WEIGHTS,
          {
            assessment_id,
            items,
          },
          get_request_config(),
        )
        .then(async ({ data }) => {
          if (data?.status === 'success') {
            await this.load_saved_weights();
            this.show_success(data?.message || 'Weights saved successfully');
            return;
          }

          this.show_error(data?.message || 'Failed to save checklist weights.');
        })
        .catch((error) => {
          this.show_error(error?.response?.data?.message || error?.response?.data?.detail || error?.message || 'Failed to save checklist weights.');
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
